import { AxiosInstance } from 'axios';

export function createPeopleApi(client: AxiosInstance) {
  return {
    async getAdmins() { const response = await client.get('/admins'); return response.data; },
    async createAdmin(data: { first_name?: string; last_name?: string; email: string; password: string; name?: string; is_operator?: boolean; access_scope?: 'company' | 'assigned_projects'; module_permissions?: any; sub_module_permissions?: any; template_key?: string | null }) {
      const response = await client.post('/admins', data);
      return response.data;
    },
    async updateAdmin(id: number, data: { first_name?: string; last_name?: string; email?: string; name?: string; password?: string; access_scope?: 'company' | 'assigned_projects'; module_permissions?: any; sub_module_permissions?: any; template_key?: string | null }) {
      const response = await client.patch(`/admins/${id}`, data);
      return response.data;
    },
    async toggleAdminActive(id: number) { const response = await client.post(`/admins/${id}/toggle-active`); return response.data; },
    async deleteAdmin(id: number) { const response = await client.delete(`/admins/${id}`); return response.data; },
    async archiveAdmin(id: number) { const response = await client.post(`/admins/${id}/archive`); return response.data; },
    async getSupervisors() { const response = await client.get('/supervisors'); return response.data; },
    async createSupervisor(data: { first_name?: string; last_name?: string; email: string; password: string; name?: string; module_permissions?: any; sub_module_permissions?: any; assigned_zone_ids?: number[]; template_key?: string | null }) {
      const response = await client.post('/supervisors', data);
      return response.data;
    },
    async updateSupervisor(id: number, data: { first_name?: string; last_name?: string; email?: string; name?: string; password?: string; module_permissions?: any; sub_module_permissions?: any; assigned_zone_ids?: number[]; template_key?: string | null }) {
      const response = await client.patch(`/supervisors/${id}`, data);
      return response.data;
    },
    async toggleSupervisorActive(id: number) { const response = await client.post(`/supervisors/${id}/toggle-active`); return response.data; },
    async deleteSupervisor(id: number, force?: boolean) {
      const response = await client.delete(`/supervisors/${id}`, {
        params: force ? { force: 'true' } : undefined,
      });
      return response.data;
    },
    async archiveSupervisor(id: number) { const response = await client.post(`/supervisors/${id}/archive`); return response.data; },
    async getAccessTemplates() { const response = await client.get('/access-templates'); return response.data; },
    async createAccessTemplate(data: {
      account_type: 'admin' | 'supervisor';
      name: string;
      description?: string;
      module_permissions: any;
      sub_module_permissions?: any;
      home_profile?: string | null;
    }) {
      const response = await client.post('/access-templates', data);
      return response.data;
    },
    async updateAccessTemplate(id: number, data: {
      name?: string;
      description?: string;
      module_permissions?: any;
      sub_module_permissions?: any;
      home_profile?: string | null;
    }) {
      const response = await client.patch(`/access-templates/${id}`, data);
      return response.data;
    },
    async deleteAccessTemplate(id: number) { const response = await client.delete(`/access-templates/${id}`); return response.data; },
    async getAccessTemplateUsage(id: number) {
      const response = await client.get(`/access-templates/${id}/usage`);
      return response.data as { users: Array<{ id: number; email: string; role: string }> };
    },
    async getUserAssignment(userId: number) {
      const response = await client.get(`/authz/users/${userId}/assignment`);
      return response.data as { assignment: import('../../types').AuthzUserAssignment | null };
    },
    async getEmployees(project_id?: number, include_inactive?: boolean, include_project_scoped?: boolean, separation_status?: 'scheduled' | 'former', week_start_date?: string, profile?: 'timesheet') {
      const params: any = {};
      if (project_id) params.project_id = project_id;
      if (week_start_date) params.week_start_date = week_start_date;
      if (include_inactive) params.include_inactive = 'true';
      if (include_project_scoped) params.include_project_scoped = 'true';
      if (separation_status) params.separation_status = separation_status;
      if (profile) params.profile = profile;
      const response = await client.get('/employees', { params });
      return response.data;
    },
    async getPeopleMode() { const response = await client.get('/people/mode'); return response.data as { interactive_enabled: boolean }; },
    async getPeopleTransferCandidates(q: string) {
      const response = await client.get('/people/transfer-candidates', { params: { q } });
      return response.data as import('../../types').PeopleTransferCandidate[];
    },
    async setPeopleMode(interactive_enabled: boolean) { const response = await client.patch('/people/mode', { interactive_enabled }); return response.data as { interactive_enabled: boolean }; },
    async getPeopleAssignments(id: number) { const response = await client.get(`/employees/${id}/assignments`); return response.data as import('../../types').PeopleAssignment[]; },
    async getPeopleStaffingBoard(week_start_date?: string) { const response = await client.get('/people/assignments', { params: { week_start_date } }); return response.data as import('../../types').PeopleStaffingBoard; },
    async getTimesheetWorkerCandidates(project_id: number, week_start_date: string) {
      const response = await client.get('/timesheets/eligibility/candidates', { params: { project_id, week_start_date } });
      return response.data as { worked_here: import('../../types').TimesheetWorkerCandidate[]; others: import('../../types').TimesheetWorkerCandidate[] };
    },
    async enableTimesheetWorkerForWeek(data: { project_id: number; employee_id: number; week_start_date: string }) { const response = await client.post('/timesheets/eligibility', data); return response.data; },
    async savePeopleAssignment(id: number, data: { action: 'assign' | 'move' | 'end'; project_id?: number; division_id?: number; from_assignment_id?: number; start_date: string; end_date?: string | null }) { const response = await client.post(`/employees/${id}/assignments`, data); return response.data; },
    async getEmployeeRecordsAttention() {
      const response = await client.get('/employees/records-attention');
      return response.data as {
        rate_check_available: boolean;
        total: number;
        items: { id: number; name: string; missing_emergency_contact: boolean; missing_rate: boolean }[];
      };
    },
    async searchEmployeeDirectory(project_id: number, q: string) {
      const response = await client.get('/employees/search-directory', {
        params: { project_id, q },
      });
      return response.data;
    },
    async getEmployeeRequests(status = 'pending') {
      const response = await client.get('/employee-requests', { params: { status } });
      return response.data;
    },
    async createEmployeeRequest(data: { first_name?: string; last_name?: string;
      request_type: 'new_employee' | 'assign_existing';
      project_id: number;
      existing_employee_id?: number;
      name?: string;
      position?: string;
      hourly_rate?: number;
      employment_type?: 'direct' | 'contractor' | 'rented';
      contractor_company?: string;
      rental_source_note?: string;
      rental_crew_id?: number;
      use_immediately?: boolean;
    }) {
      const response = await client.post('/employee-requests', data);
      return response.data;
    },
    async updateEmployeeRequest(id: number, data: { first_name?: string; last_name?: string;
      name: string;
      position?: string;
      hourly_rate?: number | null;
      employment_type?: 'direct' | 'contractor' | 'rented';
      contractor_company?: string;
      rental_source_note?: string | null;
      rental_crew_id?: number | null;
    }) {
      const response = await client.patch(`/employee-requests/${id}`, data);
      return response.data;
    },
    async approveEmployeeRequest(id: number) { const response = await client.post(`/employee-requests/${id}/approve`); return response.data; },
    async rejectEmployeeRequest(id: number, review_note?: string) {
      const response = await client.post(`/employee-requests/${id}/reject`, { review_note });
      return response.data;
    },
    async createEmployee(name: string, hourly_rate: number, position?: string, hire_date?: string, employment_type?: string, contractor_company?: string, nameParts?: { first_name: string; last_name: string }) {
      const response = await client.post('/employees', { name, hourly_rate, position, hire_date, employment_type, contractor_company, ...nameParts });
      return response.data;
    },
    async updateEmployee(id: number, data: { first_name?: string; last_name?: string;
      name?: string;
      hourly_rate?: number;
      position?: string;
      hire_date?: string;
      employment_type?: string;
      contractor_company?: string | null;
      annual_pto_days?: number;
      rate_effective_date?: string;
      rate_note?: string;
      profile_photo?: string | null;
    }) {
      const response = await client.patch(`/employees/${id}`, data);
      return response.data;
    },
    async getEmployeeRateHistory(id: number) {
      const response = await client.get(`/employees/${id}/rate-history`);
      return response.data as import('../../types').EmployeeRateHistory[];
    },
    async deleteEmployeeRateHistoryEntry(id: number, historyId: number) {
      const response = await client.delete(`/employees/${id}/rate-history/${historyId}`);
      return response.data;
    },
    async editEmployeeRateHistoryEntry(id: number, historyId: number, data: { hourly_rate?: number; effective_date?: string; note?: string }) {
      const response = await client.patch(`/employees/${id}/rate-history/${historyId}`, data);
      return response.data as import('../../types').EmployeeRateHistory;
    },
    async getEmployeeEmergencyContacts(id: number) {
      const response = await client.get(`/employees/${id}/emergency-contacts`);
      return response.data as import('../../types').EmployeeEmergencyContact[];
    },
    async createEmployeeEmergencyContact(id: number, data: {
      name: string;
      relationship?: string;
      phone?: string;
      email?: string;
      is_primary?: boolean;
      notes?: string;
    }) {
      const response = await client.post(`/employees/${id}/emergency-contacts`, data);
      return response.data as import('../../types').EmployeeEmergencyContact;
    },
    async updateEmployeeEmergencyContact(id: number, contactId: number, data: {
      name?: string;
      relationship?: string | null;
      phone?: string | null;
      email?: string | null;
      is_primary?: boolean;
      notes?: string | null;
    }) {
      const response = await client.patch(`/employees/${id}/emergency-contacts/${contactId}`, data);
      return response.data as import('../../types').EmployeeEmergencyContact;
    },
    async deleteEmployeeEmergencyContact(id: number, contactId: number) {
      const response = await client.delete(`/employees/${id}/emergency-contacts/${contactId}`);
      return response.data;
    },
    async scheduleEmployeeOffboarding(id: number, data: {
      separation_type: string;
      separation_reason?: string;
      last_day_worked?: string;
      separation_effective_date: string;
      rehire_eligible?: boolean;
      separation_notes?: string;
    }) {
      const response = await client.post(`/employees/${id}/offboarding/schedule`, data);
      return response.data as import('../../types').Employee;
    },
    async cancelEmployeeOffboarding(id: number) {
      const response = await client.post(`/employees/${id}/offboarding/cancel`);
      return response.data as import('../../types').Employee;
    },
    async finalizeEmployeeOffboarding(id: number, data?: {
      separation_type?: string;
      separation_reason?: string;
      last_day_worked?: string;
      rehire_eligible?: boolean;
      separation_notes?: string;
    }) {
      const response = await client.post(`/employees/${id}/offboarding/finalize`, data || {});
      return response.data as import('../../types').Employee;
    },
    async getCredentialTypes(includeInactive?: boolean) {
      const response = await client.get('/credential-types', { params: includeInactive ? { include_inactive: 'true' } : undefined });
      return response.data as import('../../types').CredentialType[];
    },
    async createCredentialType(data: { name: string; category?: string; requires_issue_date?: boolean; requires_expiration?: boolean; description?: string; importance?: import('../../types').CredentialImportance }) {
      const response = await client.post('/credential-types', data);
      return response.data as import('../../types').CredentialType;
    },
    async updateCredentialType(id: number, data: { name?: string; category?: string; requires_issue_date?: boolean; requires_expiration?: boolean; description?: string; is_active?: boolean; importance?: import('../../types').CredentialImportance }) {
      const response = await client.patch(`/credential-types/${id}`, data);
      return response.data as import('../../types').CredentialType;
    },
    async deleteCredentialType(id: number) {
      const response = await client.delete(`/credential-types/${id}`);
      return response.data;
    },
    async getCredentialsDashboard() {
      const response = await client.get('/credentials-dashboard');
      return response.data as import('../../types').CredentialDashboardRow[];
    },
    async getEmployeeCredentials(id: number) {
      const response = await client.get(`/employees/${id}/credentials`);
      return response.data as import('../../types').EmployeeCredential[];
    },
    async getEmployeeCredentialAttachment(id: number, credentialId: number) {
      const response = await client.get(`/employees/${id}/credentials/${credentialId}/attachment`);
      return response.data as { attachment_data: string; attachment_filename: string | null };
    },
    async createEmployeeCredential(id: number, data: {
      credential_type_id: number;
      completion_date?: string;
      expiration_date?: string;
      no_expiration?: boolean;
      card_number?: string;
      issuer?: string;
      attachment_data?: string;
      attachment_filename?: string;
      notes?: string;
    }) {
      const response = await client.post(`/employees/${id}/credentials`, data);
      return response.data as import('../../types').EmployeeCredential;
    },
    async updateEmployeeCredential(id: number, credentialId: number, data: {
      completion_date?: string | null;
      expiration_date?: string | null;
      no_expiration?: boolean;
      card_number?: string | null;
      issuer?: string | null;
      attachment_data?: string | null;
      attachment_filename?: string | null;
      notes?: string | null;
      verified?: boolean;
    }) {
      const response = await client.patch(`/employees/${id}/credentials/${credentialId}`, data);
      return response.data as import('../../types').EmployeeCredential;
    },
    async deleteEmployeeCredential(id: number, credentialId: number) {
      const response = await client.delete(`/employees/${id}/credentials/${credentialId}`);
      return response.data;
    },
    async getEmployeeDocuments(id: number) {
      const response = await client.get(`/employees/${id}/documents`);
      return response.data as import('../../types').EmployeeDocument[];
    },
    async getEmployeeDocumentAttachment(id: number, documentId: number) {
      const response = await client.get(`/employees/${id}/documents/${documentId}/attachment`);
      return response.data as { attachment_data: string; attachment_filename: string | null };
    },
    async createEmployeeDocument(id: number, data: {
      document_type: import('../../types').EmployeeDocumentType;
      label?: string;
      document_date?: string;
      expiration_date?: string;
      attachment_data: string;
      attachment_filename?: string;
      notes?: string;
    }) {
      const response = await client.post(`/employees/${id}/documents`, data);
      return response.data as import('../../types').EmployeeDocument;
    },
    async updateEmployeeDocument(id: number, documentId: number, data: {
      label?: string | null;
      document_date?: string | null;
      expiration_date?: string | null;
      attachment_data?: string | null;
      attachment_filename?: string | null;
      notes?: string | null;
    }) {
      const response = await client.patch(`/employees/${id}/documents/${documentId}`, data);
      return response.data as import('../../types').EmployeeDocument;
    },
    async deleteEmployeeDocument(id: number, documentId: number) {
      const response = await client.delete(`/employees/${id}/documents/${documentId}`);
      return response.data;
    },
    async deleteEmployee(id: number) { const response = await client.delete(`/employees/${id}`); return response.data; },
    async toggleEmployeeActive(id: number) { const response = await client.post(`/employees/${id}/toggle-active`); return response.data; },
    async getRentalCrewsDirectory() {
      const response = await client.get('/rental-crews-directory');
      return response.data as import('../../types').RentalCrewsDirectory;
    },
  };
}

export type PeopleApi = ReturnType<typeof createPeopleApi>;
